import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { VStack, HStack, StackItem } from '@astryxdesign/core/Stack';
import { Heading, Text } from '@astryxdesign/core/Text';
import { Button } from '@astryxdesign/core/Button';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Icon } from '@astryxdesign/core/Icon';
import { List, ListItem } from '@astryxdesign/core/List';
import { TextInput } from '@astryxdesign/core/TextInput';
import { TextArea } from '@astryxdesign/core/TextArea';
import { Banner } from '@astryxdesign/core/Banner';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { ArrowLeft, CheckCircle, X } from '@phosphor-icons/react';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useTenant } from '@/app/tenant-context';
import { ApiError } from '@/lib/api';
import { bookingStore } from '@/lib/booking-store';
import { dateTimeLabel, duration, money } from '@/lib/format';
import type { PublicBooking, Service } from '@/lib/types';
import { SlotPicker } from './SlotPicker';
import { formatPhoneInput, validateContact, type ContactErrors, type ContactForm } from './validation';
import { ReminderOffer } from './ReminderOffer';

type Step = 'service' | 'time' | 'contacts' | 'done';
const STEP_TITLE: Record<Step, string> = {
  service: 'Выберите услугу',
  time: 'Дата и время',
  contacts: 'Ваши контакты',
  done: 'Вы записаны',
};
const EMPTY_FORM: ContactForm = { name: '', phone: '', car: '', comment: '' };

export function BookingSheet({
  open,
  onOpenChange,
  initialServiceId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialServiceId?: string;
}) {
  const { tenant, api, slug } = useTenant();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('service');
  const [service, setService] = useState<Service | null>(null);
  const [startsAt, setStartsAt] = useState<string>('');
  const [form, setForm] = useState<ContactForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [slotNotice, setSlotNotice] = useState<string>();
  const [booking, setBooking] = useState<(PublicBooking & { token: string }) | null>(null);

  // (re)start the flow whenever the sheet opens
  useEffect(() => {
    if (!open) return;
    const preset = tenant.services.find((s) => s.id === initialServiceId) ?? (tenant.services.length === 1 ? tenant.services[0] : undefined);
    setService(preset ?? null);
    setStep(preset ? 'time' : 'service');
    setStartsAt('');
    setErrors({});
    setSlotNotice(undefined);
    setBooking(null);
    try {
      const saved = JSON.parse(localStorage.getItem(`sgx:${slug}:contact`) ?? 'null') as ContactForm | null;
      setForm(saved ? { ...saved, comment: '' } : EMPTY_FORM);
    } catch {
      setForm(EMPTY_FORM);
    }
  }, [open, initialServiceId, tenant.services, slug]);

  const submit = useMutation({
    mutationFn: async () => {
      const v = validateContact(form);
      if (!v.ok) {
        setErrors(v.errors);
        throw new ApiError('validation', 'Проверьте поля формы');
      }
      setErrors({});
      // same form + slot ⇒ same idempotency key and token (no duplicates on retry)
      const fingerprint = JSON.stringify([service!.id, startsAt, v.value.name, v.value.phone.replace(/\D/g, ''), v.value.car]);
      const pending = bookingStore.pendingFor(slug, fingerprint);
      const b = await api.createBooking({
        serviceId: service!.id,
        startsAt,
        name: v.value.name,
        phone: v.value.phone,
        car: v.value.car,
        comment: v.value.comment,
        idempotencyKey: pending.idempotencyKey,
        accessToken: pending.token,
      });
      return { ...b, token: pending.token };
    },
    onSuccess: (b) => {
      bookingStore.save(slug, { token: b.token, id: b.id, code: b.code, startsAt: b.starts_at, serviceName: b.service_name, savedAt: new Date().toISOString() });
      bookingStore.clearPending(slug);
      try {
        localStorage.setItem(`sgx:${slug}:contact`, JSON.stringify({ name: form.name, phone: form.phone, car: form.car, comment: '' }));
      } catch {
        /* ignore */
      }
      setBooking(b);
      setStep('done');
      void qc.invalidateQueries({ queryKey: ['availability'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && ['slot_unavailable', 'not_an_intake_moment', 'slot_in_past', 'slot_beyond_horizon'].includes(e.code)) {
        bookingStore.clearPending(slug);
        setSlotNotice(e.message);
        setStartsAt('');
        setStep('time');
        void qc.invalidateQueries({ queryKey: ['availability', service?.id] });
      }
      if (e instanceof ApiError) {
        const field = ({ invalid_phone: 'phone', invalid_name: 'name', invalid_car: 'car', invalid_comment: 'comment' } as const)[
          e.code as 'invalid_phone'
        ];
        if (field) setErrors({ [field]: e.message });
      }
    },
  });

  const back = useCallback(() => {
    if (step === 'contacts') setStep('time');
    else if (step === 'time' && tenant.services.length > 1) setStep('service');
  }, [step, tenant.services.length]);

  const setField = (k: keyof ContactForm) => (v: string) => {
    setForm((f) => ({ ...f, [k]: k === 'phone' ? formatPhoneInput(v) : v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const generalError =
    submit.error instanceof ApiError && !['validation', 'invalid_phone', 'invalid_name', 'invalid_car', 'invalid_comment', 'slot_unavailable'].includes(submit.error.code)
      ? submit.error.message
      : null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent aria-describedby="booking-desc">
        <DrawerHeader>
          <HStack gap={2} vAlign="center">
            {(step === 'contacts' || (step === 'time' && tenant.services.length > 1)) && (
              <IconButton label="Назад" icon={<Icon icon={ArrowLeft} />} variant="ghost" onClick={back} />
            )}
            <StackItem size="fill">
              <DrawerTitle>{STEP_TITLE[step]}</DrawerTitle>
            </StackItem>
            <IconButton label="Закрыть" icon={<Icon icon={X} />} variant="ghost" onClick={() => onOpenChange(false)} />
          </HStack>
          <DrawerDescription id="booking-desc">
            {step === 'service' && 'Цена и длительность — по прайсу студии.'}
            {step !== 'service' && step !== 'done' && service && `${service.name} · ${duration(service.duration_minutes)} · ${money(service.price_cents, tenant.currency, service.price_is_from)}`}
            {step === 'done' && 'Мы сохранили запись на этом устройстве.'}
          </DrawerDescription>
        </DrawerHeader>

        <VStack gap={4} paddingInline={4} paddingBlockEnd={6} isScrollable className="safe-bottom min-h-0">
          {step === 'service' && (
            <List density="spacious" hasDividers>
              {tenant.services.map((s) => (
                <ListItem
                  key={s.id}
                  label={s.name}
                  description={`${duration(s.duration_minutes)}${s.description ? ` · ${s.description}` : ''}`}
                  endContent={<Text weight="semibold">{money(s.price_cents, tenant.currency, s.price_is_from)}</Text>}
                  isSelected={service?.id === s.id}
                  onClick={() => {
                    setService(s);
                    setStartsAt('');
                    setStep('time');
                  }}
                />
              ))}
            </List>
          )}

          {step === 'time' && service && (
            <>
              <SlotPicker
                service={service}
                timezone={tenant.timezone}
                horizonDays={tenant.horizon_days}
                load={(id, from, days) => api.availability(id, from, days)}
                value={startsAt || null}
                onChange={(v) => {
                  setStartsAt(v);
                  if (v) setSlotNotice(undefined);
                }}
                notice={slotNotice}
              />
              <Button
                label={startsAt ? `Дальше: ${dateTimeLabel(startsAt, tenant.timezone)}` : 'Выберите время'}
                variant="primary"
                size="lg"
                width="100%"
                isDisabled={!startsAt}
                onClick={() => setStep('contacts')}
              />
            </>
          )}

          {step === 'contacts' && service && (
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                if (!submit.isPending) submit.mutate();
              }}
            >
              <VStack gap={4}>
                <Banner status="info" title={dateTimeLabel(startsAt, tenant.timezone)} description={tenant.address} />
                <TextInput label="Имя" value={form.name} onChange={setField('name')} autoComplete="name" isRequired
                  status={errors.name ? { type: 'error', message: errors.name } : undefined} hasAutoFocus />
                <TextInput label="Телефон" value={form.phone} onChange={setField('phone')} autoComplete="tel" isRequired
                  placeholder="+7 900 000-00-00" description="Студия позвонит, если что-то изменится"
                  status={errors.phone ? { type: 'error', message: errors.phone } : undefined} />
                <TextInput label="Автомобиль" value={form.car} onChange={setField('car')} isRequired placeholder="Марка и модель"
                  status={errors.car ? { type: 'error', message: errors.car } : undefined} />
                <TextArea label="Комментарий" value={form.comment} onChange={setField('comment')} isOptional rows={2} maxLength={500}
                  status={errors.comment ? { type: 'error', message: errors.comment } : undefined} />
                {generalError && <Banner status="error" title="Не получилось записаться" description={generalError} />}
                <Text type="supporting">
                  Отменить онлайн можно не позднее чем за {tenant.cancel_policy_hours} ч до визита.
                </Text>
                <Button type="submit" label="Подтвердить запись" variant="primary" size="lg" width="100%" isLoading={submit.isPending} />
              </VStack>
            </form>
          )}

          {step === 'done' && booking && (
            <VStack gap={5}>
              <HStack gap={3} vAlign="center">
                <Icon icon={CheckCircle} size="lg" color="success" />
                <Heading level={3}>Запись {booking.code} подтверждена</Heading>
              </HStack>
              {booking.is_demo && <Banner status="info" title="Это демо-запись" description="Студия ещё не запущена." />}
              <MetadataList columns="single">
                <MetadataListItem label="Услуга">{booking.service_name}</MetadataListItem>
                <MetadataListItem label="Когда">{dateTimeLabel(booking.starts_at, booking.timezone)}</MetadataListItem>
                <MetadataListItem label="Стоимость">{money(booking.price_cents, booking.currency, booking.price_is_from)}</MetadataListItem>
                <MetadataListItem label="Адрес">{booking.tenant.address}</MetadataListItem>
                <MetadataListItem label="Автомобиль">{booking.car}</MetadataListItem>
              </MetadataList>
              <ReminderOffer booking={booking} token={booking.token} />
              <Button label="Готово" variant="secondary" size="lg" width="100%" onClick={() => onOpenChange(false)} />
            </VStack>
          )}
        </VStack>
      </DrawerContent>
    </Drawer>
  );
}
